import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { User, Mail, Phone, CreditCard, GraduationCap, FileText, ArrowRight } from 'lucide-react';
import TextInput from '../../components/TextInput/TextInput';
import Select from '../../components/Select/Select';
import { registrationApi } from '../../api/registrations';
import NAV_LOGO from '../../assets/logos/World_O_Tutor.webp';
import { SEO } from '../../components/SEO';

const classOptions = [
  { value: 'Class 1-5', label: 'Primary (Class 1 - 5)' },
  { value: 'Class 6-8', label: 'Middle School (Class 6 - 8)' },
  { value: 'Class 9', label: 'Class 9' },
  { value: 'Class 10', label: 'Class 10' },
  { value: 'Class 11', label: 'Class 11 / CBSE / ICSE' },
  { value: 'Class 12', label: 'Class 12 / CBSE / ICSE' },
  { value: 'Undergraduate', label: 'College / Undergrad' },
  { value: 'Grad', label: 'Post-Graduate' },
];

const workshopOptions = [
  { value: 'AI Summer Camp', label: 'AI Summer Camp' },
  { value: 'Subject Workshop', label: 'Subject Specific Workshop' },
  { value: 'Mathematics Mastery Bootcamp', label: 'Mathematics Mastery' },
  { value: 'Robotics & STEAM Workshop', label: 'Robotics & STEAM Workshop' },
  { value: 'Coding in Python Bootcamp', label: 'Coding in Python' },
];

const currencyOptions = [
  { value: 'INR (Indian Rupee)', label: 'INR (Indian Rupee)' },
  { value: 'USD (US Dollar)', label: 'USD (US Dollar)' },
  { value: 'EUR (Euro)', label: 'EUR (Euro)' },
  { value: 'GBP (British Pound)', label: 'GBP (British Pound)' },
];

const getCurrencySymbol = (currencyStr: string) => {
  if (currencyStr?.includes('INR')) return '₹';
  if (currencyStr?.includes('USD')) return '$';
  if (currencyStr?.includes('EUR')) return '€';
  if (currencyStr?.includes('GBP')) return '£';
  return '₹';
};

export default function Registration() {
  const { register, handleSubmit, reset, watch, formState: { errors } } = useForm();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const selectedCurrency = watch('currency');

  const onSubmit = async (data: any) => {
    setLoading(true);
    try {
      const result = await registrationApi.createRegistration(data);
      if (result) {
        reset();
        navigate('/thank-you', { replace: true });
      }
    } catch (error) {
      // handle error
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO
        title="Registration - WorldoTutor"
        description="Register for programs and workshops at WorldoTutor. Fill in your details to get started."
        url="/registration"
        type="website"
      />
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
        <div className="w-full max-w-xl mx-auto bg-white rounded-[2.5rem] border border-slate-100 shadow-2xl p-6 sm:p-10 select-none" id="registration-card">
          {/* Brand Logo */}
          <div className="flex justify-center">
            <img src={NAV_LOGO} alt="WorldoTutor" className="h-14 w-auto" />
          </div>

          {/* Header decoration */}
          <div className="flex items-center justify-center gap-4 mt-6" id="card-header-deco">
            <div className="flex items-center gap-1 shrink-0">
              <div className="w-10 sm:w-16 h-0.5 bg-secondary" />
              <div className="w-2 h-2 rounded-full bg-secondary" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-black text-primary tracking-tight uppercase">
              Registration Form
            </h1>
            <div className="flex items-center gap-1 shrink-0">
              <div className="w-2 h-2 rounded-full bg-secondary" />
              <div className="w-10 sm:w-16 h-0.5 bg-secondary" />
            </div>
          </div>

          <p className="text-gray-500 font-semibold text-[15px] sm:text-base text-center mt-2.5 mb-8" id="card-subheading">
            Please fill in the details to register.
          </p>

          {/* Form */}
          <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5" id="student-registration-form" noValidate>
            <TextInput
              label="Student Name"
              name="studentName"
              register={register}
              errors={errors}
              required
              leftIcon={<User className="w-5 h-5 text-gray-400" />}
            />

            <TextInput
              label="Parent Name"
              name="parentName"
              register={register}
              errors={errors}
              required
              leftIcon={<User className="w-5 h-5 text-gray-400" />}
            />

            <TextInput
              label="Email"
              name="email"
              type="email"
              register={register}
              errors={errors}
              required
              leftIcon={<Mail className="w-5 h-5 text-gray-400" />}
            />

            <TextInput
              label="Phone"
              name="phone"
              type="tel"
              register={register}
              errors={errors}
              required
              leftIcon={<Phone className="w-5 h-5 text-gray-400" />}
            />

            <Select
              label="Select class"
              name="className"
              register={register}
              errors={errors}
              required
              options={classOptions}
              leftIcon={<GraduationCap className="w-5 h-5 text-gray-400" />}
            />

            <Select
              label="Select workshop/program"
              name="workshop"
              register={register}
              errors={errors}
              required
              options={workshopOptions}
              leftIcon={<FileText className="w-5 h-5 text-gray-400" />}
            />

            <Select
              label="Select currency"
              name="currency"
              register={register}
              errors={errors}
              required
              options={currencyOptions}
              leftIcon={<span className="text-gray-400 font-bold text-base">{getCurrencySymbol(selectedCurrency)}</span>}
            />

            <TextInput
              label="Paid Amount"
              name="paidAmount"
              type="number"
              register={register}
              errors={errors}
              required
              leftIcon={<CreditCard className="w-5 h-5 text-gray-400" />}
            />

            {/* Submit button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full h-14 mt-3 bg-secondary hover:bg-[#E05F00] active:scale-[0.98] text-white font-black text-lg rounded-xl flex items-center justify-center gap-3 transition-all duration-200 shadow-lg shadow-secondary/20 select-none cursor-pointer group disabled:opacity-70 disabled:cursor-not-allowed"
              id="btn-register-submit"
            >
              {loading && (
                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              )}
              {loading ? 'Registering...' : 'Register Now'}
              {!loading && (
                <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center shrink-0 group-hover:translate-x-0.5 transition-transform" id="btn-submit-arrow-circle">
                  <ArrowRight className="w-4 h-4 text-white" strokeWidth={3} />
                </div>
              )}
            </button>
          </form>
        </div>
      </div>
    </>
  );
}
